/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { IconButton, Stack, TextField, Typography, Box } from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

import IncDecBtnGroup from '../IncDecBtnGroup';

function QuestionCheckboxForm({
  type,
  answers,
  question,
  onChange,
  selectedOptions,
}) {
  const [specialInstructions, setSpecialInstructions] = useState([]);
  const [specialOpen, setSpecialOpen] = useState({ optionId: 0, open: false });

  useEffect(() => {
    const isSelected = selectedOptions.map((special) => ({
      optionId: special.id,
      special: special.specialInstructions,
    }));

    setSpecialInstructions(isSelected);
  }, [selectedOptions]);

  const selectedTotal = selectedOptions
    .filter((option) => option.parent === question.id)
    .reduce((acc, item) => acc + item.quantity * item.weight, 0);

  const addInstructions = (text, optionId) => {
    const comment = { optionId, special: text };
    let changed = false;

    const instructions = specialInstructions.map((special) => {
      const updatedSpecial = { ...special };

      if (updatedSpecial.optionId === optionId) {
        updatedSpecial.special = comment.special;
        changed = true;
      }

      return updatedSpecial;
    });

    if (!changed) {
      instructions.push(comment);
    }

    setSpecialInstructions(instructions);
  };

  const updateQuantity = (optionId, quantity) => {
    const newSelected = selectedOptions.map((option) =>
      option.id === optionId ? { ...option, quantity } : option
    );

    onChange(newSelected);
  };

  const numFree = question?.num_free ? `(${question?.num_free} Free)` : '';
  const minOptionsText = question.min ? `at least ${question.min}, ` : '';
  const maxOptionsText = question.max ? `${question.max}` : '';
  const chooseText = ` Choose ${minOptionsText} up to ${maxOptionsText} options`;

  const handleChange = (option) => {
    const instruction = specialInstructions.find(
      (special) => special.optionId === option.id
    );

    const existingItem = selectedOptions.find(({ id }) => id === option.id);

    if (
      !existingItem &&
      (question.max <= selectedTotal ||
        question.max < selectedTotal + (option.weight || 1))
    ) {
      return;
    }

    // Find and remove all the selected children of unselected item
    const prevItemChildren = existingItem?.child
      ? selectedOptions.reduce(
          (acc, { parent, child }) => {
            if (acc.includes(parent) && child) {
              acc.push(child);
            }

            return acc;
          },
          [existingItem.child]
        )
      : [];

    const newSelected = existingItem
      ? selectedOptions.filter(
          ({ id, parent }) =>
            id !== existingItem.id && !prevItemChildren?.includes(parent)
        )
      : selectedOptions.concat({
          ...option,
          quantity: 1,
          order: question.order,
          weight: option.weight || 1,
          specialInstructions: instruction?.special || '',
        });

    onChange(newSelected);
  };

  const addInstructionToItem = (option) => {
    const instruction = specialInstructions.find(
      (special) => special.optionId === option.id
    );

    const newSelectedOptions = selectedOptions.map((selectedOption) => {
      if (selectedOption.id === instruction.optionId) {
        return {
          ...selectedOption,
          specialInstructions: instruction?.special || '',
        };
      }

      return selectedOption;
    });

    onChange(newSelectedOptions);
  };

  return (
    <Box mt={3}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" display="inline" fontWeight={600}>
          <Stack direction="row" alignItems="center">
            <Typography mr={1}>{`${question.text} ${numFree}`}</Typography>
            {!question?.min && (
              <Typography display="inline" color="text.secondary">
                Optional
              </Typography>
            )}
            {question?.min && !(selectedTotal >= question?.min) && (
              <Typography color="error" display="inline">
                * Required
              </Typography>
            )}
            {question?.min && selectedTotal >= question?.min && (
              <CheckCircleOutlineIcon fontSize="small" color="secondary" />
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{chooseText}</Typography>
            <Typography variant="caption" color="text.secondary">
              {`${selectedTotal}/${question?.max ?? 'N/A'}`}
            </Typography>
          </Stack>
        </Typography>
        <FormGroup>
          {answers?.map((option) => {
            const selectedItem = selectedOptions.find(
              ({ id }) => id === option.id
            );

            const instruction = specialInstructions.find(
              (special) => special.optionId === option.id
            );

            const optionPrice = option.prices?.find(
              (price) => price.service === type.value
            )?.price;
            const itemQuantity = selectedItem?.quantity || 0;
            const maxRemaining = question.max - selectedTotal + itemQuantity;
            const maxPerAnswer =
              maxRemaining > question.max_per_answer
                ? question.max_per_answer
                : maxRemaining;
            const max =
              question.max_per_answer <= question.max - selectedTotal
                ? question.max_per_answer
                : maxPerAnswer;
            const isChecked = option.id === selectedItem?.id;

            return (
              <Box key={option.id}>
                <Stack direction="row" justifyContent="space-between">
                  <FormControlLabel
                    data-test-id="add-item-button"
                    onChange={() => handleChange(option)}
                    control={<Checkbox checked={isChecked} />}
                    disabled={question.max === selectedTotal && !isChecked}
                    label={
                      <>
                        <Typography display="inline">{option.text}</Typography>
                        {optionPrice && (
                          <Typography display="inline" color="secondary">
                            {` (+$${optionPrice.toFixed(2)})`}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  {selectedItem && (
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      <IncDecBtnGroup
                        min={1}
                        max={max}
                        maxTotal={question.max}
                        weight={option.weight || 1}
                        value={selectedItem.quantity}
                        selectedTotal={selectedTotal}
                        data-test-id="increment-decrement-button"
                        onChange={(val) => updateQuantity(option.id, val)}
                      />
                      <IconButton
                        data-test-id="button-special-instructions"
                        onClick={() =>
                          setSpecialOpen((state) => ({
                            optionId: option.id,
                            open: !state.open,
                          }))
                        }
                      >
                        <ChatBubbleOutlineRoundedIcon color="primary" />
                      </IconButton>
                    </Box>
                  )}
                </Stack>
                <Box>
                  {selectedItem &&
                    specialOpen.optionId === option.id &&
                    specialOpen.open && (
                      <TextField
                        rows={3}
                        multiline
                        fullWidth
                        variant="outlined"
                        data-test-id="add-note"
                        defaultValue={instruction?.special || ''}
                        onBlur={() => addInstructionToItem(option)}
                        inputProps={{ 'data-test-id': 'special-instructions' }}
                        placeholder="Add-on items listed in the Special Instructions cannot be honored"
                        onChange={(e) =>
                          addInstructions(e.target.value, option.id)
                        }
                      />
                    )}
                </Box>
              </Box>
            );
          })}
        </FormGroup>
      </Stack>
    </Box>
  );
}

QuestionCheckboxForm.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      child: PropTypes.string,
      id: PropTypes.string,
      parent: PropTypes.string,
      sort: PropTypes.number,
      text: PropTypes.string,
      prices: PropTypes.arrayOf(
        PropTypes.shape({
          service: PropTypes.string,
          price: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  question: PropTypes.shape({
    format: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
    required: PropTypes.bool,
    sub: PropTypes.bool,
    text: PropTypes.string,
    num_free: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    max_per_answer: PropTypes.number,
  }).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      order: PropTypes.number,
      parent: PropTypes.string,
      quantity: PropTypes.number,
      sort: PropTypes.number,
      specialInstructions: PropTypes.string,
      text: PropTypes.string,
      weight: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
};

export default QuestionCheckboxForm;
